import axios from 'axios';

import { Transfer } from '../../../types/transfer/Transfer';
import {
  V1_ADVANCED_INVENTORY_ITEM_TRANSFER,
  V1_ADVANCED_INVENTORY_INGREDIENT_TRANSFER,
  V2_ITEM_COGS,
  V1_INGREDIENT_COGS,
  V1_ADVANCED_INVENTORY_SUBMIT_ITEM_APPROVAL,
  V1_ADVANCED_INVENTORY_SUBMIT_INGREDIENT_APPROVAL,
} from './constants';
import { reject } from './transfer';
import { advancedTransferResponseTransformer } from './transformer';

type StatusParams =
  | 'CREATED'
  | 'APPROVED'
  | 'PARTIAL'
  | 'REJECTED'
  | 'REVISE_NEEDED';

type SubjectParams = 'RECEIVER' | 'SENDER' | 'ALL';

export type Paging = {
  start?: string;
  end?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  per_page: number;
};

export type TransferListResponse = {
  data: Transfer[];
  paging: Paging;
};

export type GetListParams = {
  keyword?: string;
  start?: string;
  end?: string;
  limit?: number;
  status?: StatusParams[];
  subject: SubjectParams;
};

export type OutletParams = {
  outletId?: string;
  outletIds: string[];
};

export async function getAdvancedInventoryItemTransfer(
  params: GetListParams,
  outlet: OutletParams
): Promise<TransferListResponse> {
  const queryParams = {
    start: params.start,
    end: params.end,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    per_page: params.limit,
    status: params?.status?.join(','),
    subject: params.subject,
    keyword: params.keyword,
  };

  const headers: any = {
    OUTLET_IDS: outlet.outletIds,
  };

  if (outlet.outletId) {
    headers.OUTLET_ID = outlet.outletId;
  }

  try {
    const { data } = await axios({
      method: 'GET',
      url: V1_ADVANCED_INVENTORY_ITEM_TRANSFER,
      params: queryParams,
      headers,
    });

    return {
      data: advancedTransferResponseTransformer(data.data.result),
      paging: data.data.paging,
    };
  } catch (e) {
    return {
      data: [],
      paging: null as unknown as Paging,
    };
  }
}

export async function getAdvancedInventoryIngredientTransfer(
  params: GetListParams,
  outlet: OutletParams
): Promise<TransferListResponse> {
  const queryParams = {
    start: params.start,
    end: params.end,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    per_page: params.limit,
    status: params?.status?.join(','),
    subject: params.subject,
    keyword: params.keyword,
  };

  const headers: any = {
    OUTLET_IDS: outlet.outletIds,
  };

  if (outlet.outletId) {
    headers.OUTLET_ID = outlet.outletId;
  }

  try {
    const { data } = await axios({
      url: V1_ADVANCED_INVENTORY_INGREDIENT_TRANSFER,
      params: queryParams,
      headers,
    });

    return {
      data: advancedTransferResponseTransformer(data.data.result),
      paging: data.data.paging,
    };
  } catch (e) {
    return {
      data: [],
      paging: null as unknown as Paging,
    };
  }
}

export function deleteAdvancedTransferItem(id: number) {
  return axios
    .delete(`${V1_ADVANCED_INVENTORY_ITEM_TRANSFER}/${id}`)
    .catch(reject);
}

export function deleteAdvancedTransferIngredient(id: number) {
  return axios.delete(`${V1_ADVANCED_INVENTORY_INGREDIENT_TRANSFER}/${id}`);
}

export function getItemCogs(id, outletId: number) {
  return axios
    .get(`${V2_ITEM_COGS}/${id}`, {
      withCredentials: true,
      params: {
        outletId,
      },
      headers: {
        CUSTOM_OUTLET_ID: null,
      },
    })
    .catch(reject);
}

export function getIngredientCogs(id, outletId: number) {
  return axios
    .get(`${V1_INGREDIENT_COGS}/${id}`, {
      withCredentials: true,
      params: {
        outletId,
      },
      headers: {
        CUSTOM_OUTLET_ID: null,
      },
    })
    .catch(reject);
}

export function submitItemTransferApproval(id, request) {
  return axios
    .post(V1_ADVANCED_INVENTORY_SUBMIT_ITEM_APPROVAL(id), request)
    .catch(reject);
}

export function submitIngredientTransferApproval(id, request) {
  return axios
    .post(V1_ADVANCED_INVENTORY_SUBMIT_INGREDIENT_APPROVAL(id), request)
    .catch(reject);
}
